import DevicesIcon from "@mui/icons-material/Devices";
import DryCleaningIcon from "@mui/icons-material/DryCleaning";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  FilterMenuButton,
  type FilterMenuControlHeight,
  type FilterMenuOption,
} from "@/components/common/FilterMenuButton";
import { AllDeviceIcon, KioskIcon, TransitionReaderIcon } from "@/components/icons";

export type DeviceType = "all" | "FITTING_ROOM" | "IN_AISLE" | "MOBILE_PICKUP" | "RFID_CENTRAL";

interface DeviceTypeFilterProps {
  selectedDeviceType: DeviceType;
  onDeviceTypeChange: (deviceType: DeviceType) => void;
  disabled?: boolean;
  className?: string;
  compact?: boolean;
  controlHeight?: FilterMenuControlHeight;
  summary?: boolean;
  includeAllOption?: boolean; // when false, hide the 'All Devices' option (used on certain pages)
}

const DeviceTypeFilter: React.FC<DeviceTypeFilterProps> = ({
  selectedDeviceType,
  onDeviceTypeChange,
  disabled = false,
  className,
  compact = false,
  controlHeight,
  summary = false,
  includeAllOption = true,
}) => {
  const { t } = useTranslation();

  const deviceTypes = React.useMemo<FilterMenuOption<DeviceType>[]>(() => {
    const list: FilterMenuOption<DeviceType>[] = [];
    if (includeAllOption) {
      list.push({
        label: t("filters.deviceTypes.all"),
        value: "all",
        icon: <AllDeviceIcon />,
        showDividerAfter: true,
      });
    }
    list.push(
      {
        label: t("filters.deviceTypes.fittingRoom"),
        value: "FITTING_ROOM",
        icon: <DryCleaningIcon />,
      },
      {
        label: t("filters.deviceTypes.inAisle"),
        value: "IN_AISLE",
        icon: <KioskIcon />,
      },
      {
        label: t("filters.deviceTypes.mobile"),
        value: "MOBILE_PICKUP",
        icon: <SmartphoneIcon />,
      },
      {
        label: t("filters.deviceTypes.central"),
        value: "RFID_CENTRAL",
        icon: <TransitionReaderIcon />,
      },
    );
    return list;
  }, [includeAllOption, t]);

  return (
    <FilterMenuButton
      selectedValue={selectedDeviceType}
      options={deviceTypes}
      onChange={onDeviceTypeChange}
      icon={<DevicesIcon />}
      showSelectedOptionIcon
      menuTitle={t("filters.deviceType")}
      disabled={disabled}
      className={className}
      compact={compact}
      controlHeight={controlHeight ?? (summary ? 56 : undefined)}
      menuAlignment="start"
      minWidth={{ xs: "100%", md: summary ? "200px" : "unset" }}
      summary={summary ? { eyebrow: t("filters.deviceType") } : undefined}
    />
  );
};

export default DeviceTypeFilter;
